// TanStack Query hooks for the Visitor-Management module. A Visitor is a
// person logged at a site; their gate activity (check-in / check-out / denied /
// escort start & end) is recorded as append-only AccessEvents scoped to that
// visitor. Mirrors packages/api/src/routes/visitors.ts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Visitor,
  VisitorResponse,
  VisitorsListResponse,
  CreateVisitorInput,
  UpdateVisitorInput,
  AccessEvent,
  AccessEventResponse,
  AccessEventsListResponse,
  CreateAccessEventInput,
  SitesListResponse,
} from '@dsr/shared-types';
import { api } from '../client';

// ---------------------------------------------------------------------------
// Visitors
// ---------------------------------------------------------------------------

export interface VisitorFilters {
  site_id?: number;
  /** Name / company substring search. */
  q?: string;
}

export function useVisitors(filters: VisitorFilters = {}) {
  const params = new URLSearchParams();
  if (filters.site_id !== undefined) params.set('site_id', String(filters.site_id));
  if (filters.q && filters.q.trim().length > 0) params.set('q', filters.q.trim());
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/visitors?${qs}` : '/api/visitors';
  return useQuery({
    queryKey: ['visitors', { ...filters }],
    queryFn: ({ signal }) => api.get<VisitorsListResponse>(url, signal),
  });
}

export function useVisitor(id: number | null) {
  return useQuery({
    queryKey: ['visitor', id],
    enabled: id !== null,
    queryFn: ({ signal }) =>
      api.get<VisitorResponse>(`/api/visitors/${id}`, signal),
  });
}

export function useCreateVisitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVisitorInput) =>
      api.post<VisitorResponse>('/api/visitors', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['visitors'] });
    },
  });
}

export interface UpdateVisitorVars extends UpdateVisitorInput {
  id: number;
}

export function useUpdateVisitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateVisitorVars) =>
      api.patch<VisitorResponse>(`/api/visitors/${id}`, patch),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['visitors'] });
      void qc.invalidateQueries({ queryKey: ['visitor', vars.id] });
    },
  });
}

export function useDeleteVisitor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/visitors/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['visitors'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Access events (append-only, nested under a visitor)
// ---------------------------------------------------------------------------

export function useAccessEvents(visitorId: number | null) {
  return useQuery({
    queryKey: ['access-events', visitorId],
    enabled: visitorId !== null,
    queryFn: ({ signal }) =>
      api.get<AccessEventsListResponse>(
        `/api/visitors/${visitorId}/access-events`,
        signal,
      ),
  });
}

export function useCreateAccessEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      visitorId,
      ...input
    }: { visitorId: number } & CreateAccessEventInput) =>
      api.post<AccessEventResponse>(
        `/api/visitors/${visitorId}/access-events`,
        input,
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['access-events', vars.visitorId] });
      void qc.invalidateQueries({ queryKey: ['visitors'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Sites lookup — populates the site picker on the create-visitor form and
// resolves a visitor's site name for display. Kept local to this module (a
// dedicated sites query module is wired during Integration).
// ---------------------------------------------------------------------------

export function useSitesLookup() {
  return useQuery({
    queryKey: ['visitors', 'sites-lookup'],
    queryFn: ({ signal }) => api.get<SitesListResponse>('/api/sites', signal),
    staleTime: 60_000,
  });
}

// Re-export the wire types callers most often need alongside the hooks.
export type { Visitor, AccessEvent };
